import React from 'react'
import { uniqueWith } from 'common/utils/arrayUtils'
import {
  getHelpPageUrl,
  HELP_ARTICLES,
} from 'common/utils/helpPageUtils'
import {
  partialResultsProviderConnectionErrorMsg,
  noResultsProviderConnectionErrorMsg,
  tooFarInFutureMsg,
  getMaxDaysInAdvanceMsg,
  getTransferMinHoursErrorMsg,
  invalidSearchDateMsg,
  noTicketsAvailableYetMsg,
  noResultsHeaderMsg,
  stillNeedHelpMsg,
  noSchedulesFoundMsg,
} from './errors.messages'

export const errorCodes = {
  PROVIDER_CONNECTION_ERROR: 'R2R_PROVIDER_CONNECTION_ERROR',
  SEARCH_DATE_TOO_FAR_IN_FUTURE: 'R2R_SCHEDULE_SEARCH_DATES_ERROR',
  NO_SCHEDULES_FOUND: 'R2R_NO_SCHEDULES_FOUND_ERROR',
}

export const errorCodeDescriptions = {
  getPartialResultsProviderConnectionError: () => partialResultsProviderConnectionErrorMsg,
  getNoResultsProviderConnectionError: () => noResultsProviderConnectionErrorMsg,
  getSearchDatesTooFarInFuture: () => tooFarInFutureMsg,
  getMaximumDaysInAdvance: (maxDaysInAdvance) => getMaxDaysInAdvanceMsg(maxDaysInAdvance),
  getTransferMinimumSearchHours: (minSearchHours) => getTransferMinHoursErrorMsg(minSearchHours),
  getInvalidSearchDateProvided: () => invalidSearchDateMsg,
  getNoSchedulesFound: () => noSchedulesFoundMsg,
}

export const getApiErrorHelpLink = (apiErrors, className) => {
  const errorCode = getErrorCode(apiErrors)
  const helpLink = getHelpLinkForErrorCode(errorCode)

  if (helpLink) {
    return (
      <p>
        <a className={className} href={helpLink}>
          {stillNeedHelpMsg}
        </a>
      </p>
    )
  }
  return null
}

export const getApiErrorHeading = (apiErrors, modeString) => {
  const errorCode = getErrorCode(apiErrors)

  // Use error code to determine which heading to show
  switch (errorCode) {
    case errorCodes.SEARCH_DATE_TOO_FAR_IN_FUTURE:
      return noTicketsAvailableYetMsg
    default:
      return noResultsHeaderMsg(modeString)
  }
}

export const getApiErrorMessages = (apiErrors) => {
  if (apiErrors) {
    // We only want to display errors with an error code
    // (no error code means a system exception we just want to log)
    const apiErrorsToDisplay = getDistinctApiErrorMessagesToDisplay(apiErrors)

    // Each error code may map to one or more error message strings
    // We'll render each to a separate <p> element
    return apiErrorsToDisplay
      .map((error) => getMessagesFromErrorCode(error))
      .reduce((a, b) => a.concat(b))
  }
  return []
}

const getDistinctApiErrorMessagesToDisplay = (apiErrors) => {
  // We only want to display errors with an error code
  // (no error code means a system exception we just want to log)
  const apiErrorsToDisplay = apiErrors.filter((error) => !!error.errorCode)

  // Remove any duplicate error messages (different providers may have returned the same error)
  const distinctErrors = uniqueWith(apiErrorsToDisplay, (error1, error2) => {
    return error1.errorCode == error2.errorCode
  })

  // Exclude any generic 'no schedules found' errors if a more specific warning is also returned
  const distinctErrorsToDisplay = distinctErrors.filter((error) => {
    if (error.errorCode == errorCodes.NO_SCHEDULES_FOUND) {
      // return true iif no other error has been returned
      return distinctErrors.length === 1
    }
    return true
  })

  return distinctErrorsToDisplay
}

export const getMessagesFromErrorCode = (apiError) => {
  // check the error code to determine what message to display
  switch (apiError.errorCode) {
    case errorCodes.PROVIDER_CONNECTION_ERROR:
      return [errorCodeDescriptions.getNoResultsProviderConnectionError()]
    case errorCodes.SEARCH_DATE_TOO_FAR_IN_FUTURE:
      return getSearchDatesErrorMessage(apiError)
    case errorCodes.NO_SCHEDULES_FOUND:
      return getNoSchedulesFoundErrorMessage(apiError)
    default:
      return []
  }
}

const getNoSchedulesFoundErrorMessage = (errorDetail) => {
  const errMessages = [errorCodeDescriptions.getNoSchedulesFound()]

  if (errorDetail.maxAdvancePurchaseDays) {
    errMessages.push(
      errorCodeDescriptions.getMaximumDaysInAdvance(
        errorDetail.maxAdvancePurchaseDays
      )
    )
  }

  return errMessages
}

const getSearchDatesErrorMessage = (errorDetail) => {
  const errMessages = [errorCodeDescriptions.getSearchDatesTooFarInFuture()]

  if (errorDetail.maxAdvancePurchaseDays) {
    errMessages.push(
      errorCodeDescriptions.getMaximumDaysInAdvance(
        errorDetail.maxAdvancePurchaseDays
      )
    )
  } else if (errorDetail.minAdvancePurchaseHours) {
    errMessages.push(
      errorCodeDescriptions.getTransferMinimumSearchHours(
        errorDetail.minAdvancePurchaseHours
      )
    )
  } else {
    errMessages.push(
      errorCodeDescriptions.getInvalidSearchDateProvided()
    )
  }
  return errMessages
}

const getErrorCode = (apiErrors) => {
  if (apiErrors) {
    const error = apiErrors.find((err) => !!err.errorCode)
    return error ? error.errorCode : null
  }
  return null
}

const getHelpLinkForErrorCode = (errorCode) => {
  if (!errorCode) {
    return null
  }

  return `${getHelpPageUrl(HELP_ARTICLES.NO_TICKETS)}?utm_source=tickets`
}
